import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Youtube, Cookie } from 'lucide-react';
import { PremiumLogo } from './PremiumLogo';
import { Button } from './ui/button';

export const Footer = forwardRef<HTMLElement>((_, ref) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer ref={ref} className="glass-strong border-t border-border/40 mt-auto">
      <div className="container mx-auto px-4 py-8">

        <div className="grid gap-8 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="mb-4">
              <PremiumLogo size="sm" showTagline={false} />
            </div>
            <p className="text-sm text-muted-foreground max-w-sm">
              Free, secure, and private disposable email service. Protect your inbox from spam 
              and keep your personal email address safe.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground font-normal"
                  onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
                >
                  <Cookie className="w-3.5 h-3.5 mr-1.5" />
                  Manage Cookies
                </Button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {currentYear} TempVault. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a 
              href="https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="YouTube Channel"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-destructive fill-destructive" />
              <span>by</span>
              <Link to="/creator" className="font-medium hover:text-foreground transition-colors">
                Krish (futureframeai)
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';
